import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ScanLine, Trash2, X } from 'lucide-react'
import {
  apiErrorMessage,
  createTablet,
  deleteTablet,
  updateTablet,
} from '@/services/api'
import { EDITIONS, type Machine } from '@/types/machine'
import {
  IMEI_PATTERN,
  SCANNED_FIELDS,
  TABLET_SERIAL_PATTERN,
  parseScannedCode,
  type Tablet,
  type TabletInput,
} from '@/types/tablet'
import BarcodeScannerDialog from '@/components/BarcodeScannerDialog'
import ConfirmDialog from '@/components/ConfirmDialog'

interface TabletDialogProps {
  open: boolean
  // null なら新規登録、指定があれば編集
  tablet: Tablet | null
  machines: Machine[]
  onClose: () => void
  onSaved: () => void
}

const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'

const emptyForm: TabletInput = {
  name: '',
  model: '',
  edition: '',
  serialNumber: '',
  imei: '',
  productNumber: '',
  mfgDate: '',
  linkedMachineId: '',
  note: '',
}

export default function TabletDialog({
  open,
  tablet,
  machines,
  onClose,
  onSaved,
}: TabletDialogProps) {
  const { t } = useTranslation()

  const [form, setForm] = useState<TabletInput>(emptyForm)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setForm({ ...emptyForm, ...(tablet ?? {}) })
    setScannerOpen(false)
    setConfirmDelete(false)
    setError(null)
  }, [open, tablet])

  const setField = (field: keyof TabletInput, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  // スキャン結果は空欄の項目にだけ反映する（手入力・既存値を上書きしない）
  const handleScannedCode = (raw: string) => {
    const parsed = parseScannedCode(raw)
    setForm((prev) => {
      const next = { ...prev }
      for (const [field, value] of Object.entries(parsed)) {
        const key = field as keyof TabletInput
        if (!next[key]) next[key] = value
      }
      return next
    })
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      tablet ? updateTablet(tablet.tabletId, form) : createTablet(form),
    onSuccess: () => onSaved(),
    onError: (err) => setError(apiErrorMessage(err) ?? t('tabletDialog.error')),
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteTablet(tablet!.tabletId),
    onSuccess: () => onSaved(),
    onError: (err) => {
      setConfirmDelete(false)
      setError(apiErrorMessage(err) ?? t('tabletDialog.deleteError'))
    },
  })

  if (!open) return null

  const serialValid =
    !form.serialNumber || TABLET_SERIAL_PATTERN.test(form.serialNumber)
  const imeiValid = !form.imei || IMEI_PATTERN.test(form.imei)
  // 空のレコードを作らない（API 側と同じ制約）
  const hasIdentifier = Boolean(form.name || form.serialNumber || form.imei)

  // 紐付け先の選択肢は未削除の機材。紐付け済みの機材は削除されていても選択肢に残す
  const machineOptions = machines
    .filter((m) => !m.deletedAt || m.machineId === form.linkedMachineId)
    .sort((a, b) => (b.serialNumber || '').localeCompare(a.serialNumber || ''))
  const machineLabel = (machine: Machine) =>
    `${machine.serialNumber ?? t('detail.noSerial')}${
      machine.customer ? ` (${machine.customer})` : ''
    }`

  const captured = SCANNED_FIELDS.filter((field) => form[field]).map(
    (field) => ({
      label: t(`tablets.${field}`),
      value: form[field] as string,
    })
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {tablet ? t('tabletDialog.editTitle') : t('tabletDialog.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <button
              onClick={() => setScannerOpen(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
            >
              <ScanLine className="h-4 w-4" />
              {t('tabletDialog.scan')}
            </button>
            <p className="mt-1 text-xs text-gray-500">
              {t('tabletDialog.scanHint')}
            </p>
          </div>

          <div className="grid grid-cols-[1fr_7rem] gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tablets.name')}
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder={t('tabletDialog.namePlaceholder')}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('machines.edition')}
              </label>
              <select
                value={form.edition}
                onChange={(e) => setField('edition', e.target.value)}
                className={inputClass}
              >
                <option value="">-</option>
                {EDITIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tablets.model')}
              </label>
              <input
                type="text"
                value={form.model}
                onChange={(e) => setField('model', e.target.value)}
                placeholder="TB330XU"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tablets.serialNumber')}
              </label>
              <input
                type="text"
                value={form.serialNumber}
                onChange={(e) => setField('serialNumber', e.target.value)}
                placeholder="HA2FYDME"
                className={inputClass}
              />
              {!serialValid && (
                <p className="mt-1 text-xs text-red-600">
                  {t('tabletDialog.serialError')}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tablets.imei')}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={form.imei}
                onChange={(e) => setField('imei', e.target.value)}
                placeholder="861420080450933"
                className={inputClass}
              />
              {!imeiValid && (
                <p className="mt-1 text-xs text-red-600">
                  {t('tabletDialog.imeiError')}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tablets.productNumber')}
              </label>
              <input
                type="text"
                value={form.productNumber}
                onChange={(e) => setField('productNumber', e.target.value)}
                placeholder="ZADB0291JP"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tablets.mfgDate')}
              </label>
              <input
                type="date"
                value={form.mfgDate}
                onChange={(e) => setField('mfgDate', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('tablets.linkedMachine')}
              </label>
              <select
                value={form.linkedMachineId}
                onChange={(e) => setField('linkedMachineId', e.target.value)}
                className={inputClass}
              >
                <option value="">{t('tablets.unlinked')}</option>
                {machineOptions.map((machine) => (
                  <option key={machine.machineId} value={machine.machineId}>
                    {machineLabel(machine)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {t('tabletDialog.linkedMachineHint')}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('tablets.note')}
            </label>
            <textarea
              value={form.note}
              onChange={(e) => setField('note', e.target.value)}
              rows={2}
              className={inputClass}
            />
          </div>

          {!hasIdentifier && (
            <p className="text-xs text-gray-500">
              {t('tabletDialog.identifierRequired')}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t bg-gray-50">
          <div>
            {tablet && (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-700 bg-white border border-red-200 rounded-md hover:bg-red-50 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                {t('tabletDialog.delete')}
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={
                !serialValid ||
                !imeiValid ||
                !hasIdentifier ||
                saveMutation.isPending
              }
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saveMutation.isPending
                ? t('tabletDialog.submitting')
                : tablet
                  ? t('tabletDialog.update')
                  : t('tabletDialog.submit')}
            </button>
          </div>
        </div>
      </div>

      <BarcodeScannerDialog
        open={scannerOpen}
        captured={captured}
        onCode={handleScannedCode}
        onClose={() => setScannerOpen(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={t('tabletDialog.deleteConfirmTitle')}
        message={t('tabletDialog.deleteConfirmMessage', {
          name: tablet?.name || tablet?.serialNumber || tablet?.imei || '-',
        })}
        confirmLabel={t('tabletDialog.delete')}
        danger
        busy={deleteMutation.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  )
}
